import type { UseServiceFetcherOptions, ServiceMutationOptions } from "./_type";
import type {
  BaseResponse,
  BaseErrorResponse,
  ServiceResponseFetcherParams,
  ServiceResponseMutate,
  ServiceResponseMutateParams,
  ServiceResponseQuery,
} from "~/common/types/api.type";

import { useCallback, useEffect, useMemo, useState } from "react";

import { AxiosError } from "axios";
import _ from "lodash";

import { useLayoutContext } from "~/common/contexts/layout";
import { useApp } from "~/libs/antd";

/**
 * Hook for handling fetcher (GET) responses
 */
export const useServiceFetcherResponse = <TResponse, TData = TResponse>(
  response: ServiceResponseFetcherParams<TResponse>,
  options: UseServiceFetcherOptions<TResponse, TData> = {}
): ServiceResponseQuery<TData> => {
  const {
    postProcessor: transform,
    withLayoutLoading = false,
    showError = true,
  } = options;

  /** useApp */
  const { message } = useApp();

  /** useLayoutContext */
  const { setLoading } = useLayoutContext();

  /** swrResponse */
  const { error, isLoading, isValidating, mutate: reload } = response;

  /** data */
  const [data, setData] = useState<TData>();

  /** loading */
  const loading = useMemo(
    () => isLoading || isValidating,
    [isLoading, isValidating]
  );

  /**
   ** Effect - loading layout level
   */
  useEffect(() => {
    if (withLayoutLoading) {
      setLoading?.(loading);
    }
  }, [loading, setLoading, withLayoutLoading]);

  /**
   ** Effect - Handle error
   */
  useEffect(() => {
    if (showError && error) {
      message.error(error.response?.data?.message ?? error.message);
    }
  }, [error, message, showError]);

  /**
   ** Effect - Handle response
   */
  useEffect(() => {
    if (response.data) {
      const data = _.cloneDeep(response.data);
      const transformedData = transform
        ? transform(data)
        : (data as unknown as TData);
      setData(transformedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response.data]);

  /** return */
  return useMemo(
    () => ({ loading, data, error, reload }),
    [loading, data, error, reload]
  );
};

/**
 * Hook for handling mutation (POST, PUT, DELETE) responses
 */
export const useServiceMutationResponse = <
  TRequest,
  TResponse,
  TData = TResponse,
  TParams = TRequest
>(
  response: ServiceResponseMutateParams<TRequest, TResponse>,
  options: ServiceMutationOptions<TRequest, TResponse, TData, TParams> = {}
): ServiceResponseMutate<TRequest, TData> => {
  /** Extract options */
  const {
    withLayoutLoading = false,
    showError = true,
    successMessage,
    preProcessor,
    postProcessor,
    onSuccess,
    onFinished,
  } = options;

  /** useApp */
  const { message } = useApp();

  /** useLayoutContext */
  const { setLoading } = useLayoutContext();

  /** swrResponse */
  const { isMutating: loading, trigger, error } = response;

  /** data */
  const [data, setData] = useState<TData>();

  /**
   ** Effect - loading layout level
   */
  useEffect(() => {
    if (withLayoutLoading) {
      setLoading?.(loading);
    }
  }, [loading, setLoading, withLayoutLoading]);

  /**
   ** Function - Handle error
   */
  const onError = useCallback(
    (err: AxiosError<BaseErrorResponse>) => {
      if (showError) {
        message.error(err.response?.data?.message ?? err.message);
      }
    },
    [message, showError]
  );

  /**
   ** Function - Handle success
   */
  const onSuccessCallback = useCallback(() => {
    if (successMessage) {
      message.success(successMessage);
    }
    onSuccess?.();
  }, [message, successMessage, onSuccess]);

  /**
   ** Function - execute
   */
  const execute = useCallback(
    async (req: TRequest) => {
      try {
        // Type assertion to handle SWR mutation trigger overloads
        const mutationTrigger = trigger as (
          arg: TParams,
          options?: {
            onError?: (error: AxiosError<BaseErrorResponse>) => void;
            onSuccess?: () => void;
          }
        ) => Promise<BaseResponse<TResponse>>;

        const request = preProcessor
          ? preProcessor(req)
          : (req as unknown as TParams);

        const result = await mutationTrigger(request, {
          onError,
          onSuccess: onSuccessCallback,
        });

        if (result && postProcessor) {
          const transformedData = postProcessor(result);
          setData(transformedData);
          return transformedData;
        }

        return result as unknown as TData;
      } finally {
        onFinished?.();
      }
    },
    [
      trigger,
      preProcessor,
      onError,
      onSuccessCallback,
      postProcessor,
      onFinished,
    ]
  );

  /** return */
  return useMemo(
    () => ({
      loading,
      data,
      error,
      execute: execute as ServiceResponseMutate<TRequest, TData>["execute"],
    }),
    [loading, data, error, execute]
  );
};
